/* eslint-disable react/no-danger -- when updating this component, consider replacing dangerous jsx properties */
import type { RefObject } from 'react';
import React, { PureComponent, Fragment } from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { FormattedMessage, injectIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { getContentAppearanceForAtlaskit } from '@confluence/content-appearance';
import { DeepLinkHandler } from '@confluence/deep-link-handler';
import { ATLASSIAN_SUPPORT_URL } from '@confluence/error-boundary';
import { RENDERER, startMacroExperience, stopMacroExperience } from '@confluence/macro-tracker';
import { ScrollToHashLinkHandler } from '@confluence/scroll';
import { PerformanceMetrics, RendererTTIMarker } from '@confluence/view-content-perf-metrics';

import { filterADNodesByType } from '../../lib/extensions-common';

import MediaViewer from '../MediaViewer';
import type { ImageAlignmentMode, FileMediaMetaData } from '../MediaViewer/types';
import Renderer from '../Renderer';
import type { ExternalShareContentRendererComponentProps } from './types';
import { loadContentRendererWRM } from './loadContentRendererWRM';
import { DynamicAnalyticsListener, mediaChannel } from '@confluence/unique-page-load-id';

const ID_REQUIRED_TO_AVOID_BREAKING_MACROS_THAT_RELY_ON_DOM_SELECTORS = 'main-content';
const CLASSNAME_REQUIRED_TO_AVOID_BREAKING_MACROS_THAT_RELY_ON_DOM_SELECTORS = 'wiki-content';

const ccContentExperienceSlackURL = 'https://atlassian.slack.com/messages/CFGRZK3MF';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionIntro = styled.div({
	paddingBottom: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CodeBlock = styled.pre({
	whiteSpace: 'normal',
	border: `1px solid ${token('color.border', '#dddddd')}`,
	paddingLeft: token('space.100', '8px'),
	marginTop: 0,
	width: '300px',
	height: '48px',
	overflowY: 'scroll',
});

const RENDERER_STYLE = {
	margin: 'auto',
	width: '100%',
};

const getMediaFromADF = (adf): FileMediaMetaData[] => {
	return filterADNodesByType(adf, 'media').map(({ attrs }) => ({
		align: 'center' as ImageAlignmentMode,
		id: attrs.id,
		mediaItemType: attrs.type,
		collectionName: attrs.collection,
	}));
};

const renderDeepLinkHandler = (scrollTo, refOfNodeToWatchForChange) => () => {
	const scrollToId =
		scrollTo && scrollTo[0] === '#' && scrollTo.length > 1 ? scrollTo.slice(1) : null;

	if (refOfNodeToWatchForChange && scrollToId) {
		return (
			<DeepLinkHandler
				scrollTargetId={scrollToId}
				refOfNodeToWatchForChange={refOfNodeToWatchForChange}
			/>
		);
	}
	return <Fragment />;
};

const renderPerformanceComponents = (revision, contentId, appearance) => () => (
	<PerformanceMetrics contentId={contentId} appearance={appearance} fabric revision={revision} />
);

export const ExternalShareContentRendererComponent = withAnalyticsEvents()(
	injectIntl(
		class ExternalShareContentRendererComponent extends PureComponent<
			ExternalShareContentRendererComponentProps & WithAnalyticsEventsProps & WrappedComponentProps
		> {
			refOfNodeToWatchForChange: RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

			componentDidMount() {
				const { contentId } = this.props;

				if (this.props.adfError) {
					this.props.createAnalyticsEvent!({
						type: 'sendTrackEvent',
						data: {
							action: 'error',
							actionSubject: 'adf.parsing.error',
							source: 'contentRendererComponent',
							attributes: {
								errorMessage: this.props.adfError,
							},
						},
					}).fire();

					const showStagingError =
						process.env.CLOUD_ENV === 'staging' ||
						process.env.CLOUD_ENV === 'hello' ||
						process.env.CLOUD_ENV === 'branch';
					if (showStagingError) {
						void this.props.flags.showErrorFlag(this._getStagingErrorText(this.props.adfError));
					} else {
						void this.props.flags.showErrorFlag(this._getProductionErrorText());
					}
				}

				setTimeout(() => void loadContentRendererWRM(), 0);

				startMacroExperience(RENDERER, contentId);
			}

			componentWillUnmount() {
				const { contentId } = this.props;
				stopMacroExperience(RENDERER, contentId);
			}

			_getProductionErrorText() {
				return {
					title: this.props.intl.formatMessage({
						id: 'adf.translation.error.in.render.title.prod',
						defaultMessage: 'Error displaying content',
					}),
					description: (
						<div
							dangerouslySetInnerHTML={{
								__html: this.props.intl.formatMessage(
									{
										id: 'adf.translation.error.in.render.description.prod',
										defaultMessage:
											// eslint-disable-next-line no-restricted-syntax
											'Refresh the page and try again. If the problem persists, contact <a>Atlassian Support</a>.',
									},
									{
										a: (chunks: React.ReactNode[]) => (
											<a target="_blank" href={ATLASSIAN_SUPPORT_URL} rel="noreferrer noopener">
												{chunks}
											</a>
										),
									},
								) as string,
							}}
						/>
					),
				};
			}

			_getStagingErrorText(errorDetails) {
				return {
					title: this.props.intl.formatMessage({
						id: 'adf.translation.error.in.render.title.staging',
						defaultMessage: 'Error displaying content',
					}),
					description: (
						<Fragment>
							<DescriptionIntro
								dangerouslySetInnerHTML={{
									__html: this.props.intl.formatMessage(
										{
											id: 'adf.translation.error.in.render.description.staging',
											defaultMessage:
												// eslint-disable-next-line no-restricted-syntax
												'An ADF error is preventing your content from being displayed. Discuss further in the <a>#cc-content-experience</a> Slack channel.',
										},
										{
											a: (chunks: React.ReactNode[]) => (
												<a
													target="_blank"
													href={ccContentExperienceSlackURL}
													rel="noreferrer noopener"
												>
													{chunks}
												</a>
											),
										},
									) as string,
								}}
							/>
							<FormattedMessage id="editor.adf.parsing.error.details" />
							<CodeBlock>{errorDetails}</CodeBlock>
						</Fragment>
					),
				};
			}

			renderMediaViewer() {
				const { adf, contentId } = this.props;

				if (!contentId || !adf) {
					return null;
				}

				return <MediaViewer parentId={contentId} media={getMediaFromADF(adf)} />;
			}

			render() {
				window.performance.mark('CFP-63.view.page.classic.contentrenderer.rendered');

				const { adf, contentId, contentType, appearance, scrollTo, revision } = this.props;

				if (!contentId || !adf) {
					return null;
				}

				const atlaskitAppearance = getContentAppearanceForAtlaskit(appearance);

				return (
					<div
						id="content"
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className="page view"
						ref={this.refOfNodeToWatchForChange as any}
					>
						<ScrollToHashLinkHandler anchorContainer={this.refOfNodeToWatchForChange} />
						<RendererTTIMarker
							revision={revision}
							renderWhenReady={renderPerformanceComponents(revision, contentId, atlaskitAppearance)}
						>
							<DynamicAnalyticsListener channelParam={mediaChannel}>
								<Renderer
									id={ID_REQUIRED_TO_AVOID_BREAKING_MACROS_THAT_RELY_ON_DOM_SELECTORS}
									allowInlineComments
									allowHighlightActions
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									className={CLASSNAME_REQUIRED_TO_AVOID_BREAKING_MACROS_THAT_RELY_ON_DOM_SELECTORS}
									appearance={atlaskitAppearance}
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									style={RENDERER_STYLE}
									adf={adf}
									objectId={contentId}
									contentType={contentType}
									renderWhenReady={renderDeepLinkHandler(scrollTo, this.refOfNodeToWatchForChange)}
								/>
							</DynamicAnalyticsListener>
						</RendererTTIMarker>
						{this.renderMediaViewer()}
					</div>
				);
			}
		},
	),
);
